import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadGuidesByCategory } from '../../utils/cmsContentLoader';
import { Seo } from '../shared';
import KnowledgeHubLayout from '../explore/KnowledgeHubLayout';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import { TRANSPORT_HUB } from '../../data/knowledgeHubContent';
import { OFFICIAL_SOURCES, LAST_VERIFIED } from '../../data/officialSources';
import '../../styles/explore.scss';
import '../../styles/guides.scss';

function TransportationHubPage() {
	const [guides, setGuides] = useState([]);

	useEffect(() => {
		loadGuidesByCategory('transportation').then(setGuides);
	}, []);

	const hub = TRANSPORT_HUB;

	return (
		<KnowledgeHubLayout
			touristSection="plan"
			touristCurrent="Getting around"
			planStepId="transport"
			hideRelated
			planBookingPanel
			seo={(
				<Seo
					title="Transportation in Nepal — Flights, Buses, Jeeps & More"
					path="/transportation"
					description="Complete guide to getting around Nepal — domestic flights, tourist buses, private jeeps, Pathao ride-apps, helicopter, trekking access, road conditions, and border crossings."
				/>
			)}
			hero={{ image: hub.hero, eyebrow: hub.eyebrow, title: hub.title, lead: 'Nepal has no single transport system — you will fly, bus, jeep, walk, and ride-app your way through the country. This hub answers how to reach every major destination and what each option actually costs and takes.' }}
			quickFacts={hub.quickFacts}
			intro={{ title: 'How travelers actually move in Nepal', text: 'The right transport depends on where you are going — cities, lowland parks, and high Himalaya each have different answers.', body: 'Kathmandu and Pokhara have flights, buses, and ride-apps. Chitwan is bus or short flight. Everest and Annapurna treks start with a flight or long drive, then feet only. Upper Mustang needs a jeep on rough roads. There is no universal answer — only the right answer for your route.' }}
			faqs={hub.faqs}
		>
			<DiscoverySection eyebrow="By mode" title="Every way to travel in Nepal">
				<div className="info-card-grid">
					{hub.modes.map((m) => (
						<div key={m.id} id={m.id} className="info-card">
							{m.image && (
								<div className="info-card__image">
									<img src={m.image} alt={m.name} loading="lazy" />
								</div>
							)}
							<div className="info-card__body">
								<h3 className="info-card__title">{m.name}</h3>
								<p className="info-card__text">{m.summary}</p>
								<ul>
									{m.details.map((d) => <li key={d}>{d}</li>)}
								</ul>
							</div>
						</div>
					))}
				</div>
			</DiscoverySection>

			<OfficialCitation
				sourceName={OFFICIAL_SOURCES.caan.name}
				sourceUrl={OFFICIAL_SOURCES.caan.url}
				lastVerified={LAST_VERIFIED}
				subjectToChange
			>
				<p>Domestic flight schedules, airport operations, and aviation safety rules are regulated by CAAN. Airline timetables and fares change frequently — confirm with the airline or CAAN before booking mountain routes such as Lukla.</p>
			</OfficialCitation>

			<DiscoverySection eyebrow="Road reality" title="Road conditions by corridor" intro="Mountain roads are not highways — monsoon, landslides, and night driving add real risk." alt>
				<div className="climate-table-wrap">
					<table className="climate-table">
						<thead>
							<tr>
								<th>Route</th>
								<th>Condition</th>
								<th>Best season</th>
							</tr>
						</thead>
						<tbody>
							{hub.roadConditions.map((r) => (
								<tr key={r.corridor}>
									<td><strong>{r.corridor}</strong></td>
									<td>{r.condition}</td>
									<td>{r.best}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</DiscoverySection>

			<DiscoverySection eyebrow="Detailed guides" title="Route-by-route articles">
				<div className="guide-grid">
					{guides.map((g) => (
						<Link key={g.slug} to={`/guides/${g.slug}`} className="guide-card">
							<h3 className="guide-card__title">{g.title}</h3>
							{g.summary && <p className="guide-card__summary">{g.summary}</p>}
						</Link>
					))}
				</div>
			</DiscoverySection>
		</KnowledgeHubLayout>
	);
}

export default TransportationHubPage;
